import React, { Component } from "react";
import Select from "react-select";
import SelectCreateAsync from "react-select/async-creatable";

import { apiRequest } from "../../hooks/apiRequest";
import { formatCurrency, formatNumber, money, toast } from "../../utils/helpers";
import { normalizarUnidades, opcionesUnidades, filaUnidadDeLinea } from "../../utils/unidades";

// Los precios de venta se guardan redondeados a 2 decimales.
const redondear = (valor) => Math.round((parseFloat(valor) || 0) * 100) / 100;

const tienePrecioPaquetePropio = (precio) =>
    precio.precio_paquete != null && precio.precio_paquete !== "";

export default class ProductoCompra extends Component {
    state = {
        cantidad: 1,
        costo: "",
        viejoCosto: 0,
        precio: 0,
        descuento: 0,
        costoMin: 0.01,
        subtotal: 0,
        disponible: 0,
        impuesto: 0,
        referencia: null,
        impuesto_id: null,
        precios: [],
        grupo: null,
        valid: true,
        formaDeCalculo: 'total',
		total: 0,
        selected: {},
        unidades: [],
        unidadSeleccionada: null,
    };
    componentDidMount() {
        const {item} = this.props;
        if (item) {
            this.setState({
                formaDeCalculo: 'total',
                costo: item.costo || 0,
                total: item.total || 0,
                cantidad: item.cantidad,
                precio: item.total / item.cantidad,
                descuento: item.descuento || 0,
                isItem: item.isItem,
                referencia: item.referencia,
                disponible: item.item?.cantidad || 1,
                impuesto: item.impuesto ? item.impuesto?.porcentaje * 100 : 0,
                impuesto_id: item.impuesto?.id || 0,
                precios: item.precios || [{}],
                grupo: item.grupo,
                ...this.unidadesDeLinea({ unidades: item.unidades, unidad: item.unidad }, item),
                selected: item.nombre ? {
                    label: `${item?.referencia} - ${item?.nombre}`,
                    value: item.item_id
                } : null
            });
            setTimeout(() => {
                this.actualizarTotal(true);
            }, 1000);
        }
    }
    componentDidUpdate() {
        // Form.js asigna item.productoNuevo en sitio (mismo objeto item), así que
        // se compara contra el último visto y no contra prevProps.
        const nuevo = this.props.item?.productoNuevo;

        if (nuevo && nuevo !== this.productoNuevoVisto) {
            this.productoNuevoVisto = nuevo;
            this.setState((prevState) => this.unidadesDeLinea(
                this.presentacionesDeProductoNuevo(nuevo),
                { unidad: prevState.unidadSeleccionada?.value }
            ));
        }
    }
    // La unidad y el empaque elegidos en el modal, con la forma de
    // `producto.unidades`, para poder comprar el producto nuevo por empaque.
    presentacionesDeProductoNuevo = (nuevo) => {
        const unidad = nuevo.unidad?.value || 'UND';
        const cantidadEmpaque = parseFloat(nuevo.cantidadEmpaque);

        return {
            unidad,
            unidades: [
                { unidad_id: nuevo.unidad?.id ?? null, unidad, factor: 1, es_base: true },
                ...(nuevo.empaque && cantidadEmpaque > 1
                    ? [{ unidad_id: nuevo.empaque.id ?? null, unidad: nuevo.empaque.value, factor: cantidadEmpaque, es_base: false }]
                    : []),
            ],
        };
    };
    onChangeInput = async (name, value) => {
        if (name === 'descuento') {
            if (this.props.tipoDescuento === 'Porcentaje' && parseFloat(value) > 100) {
                toast('El descuento no puede ser major al 100%', 'error');
                return;
            }
            if (this.props.tipoDescuento === 'Monto Fijo' && parseFloat(value) > this.state.costo) {
                toast('El descuento no puede ser mayor al importe', 'error');
                return;
            }
        }

        await this.setState({
            [name]: value,
        });

        this.actualizarTotal();
    };
    onChangeImpuesto = async (value) => {
        const impuesto = this.props.impuestos.filter(i => i.id === value)[0];

        await this.setState({
            impuesto_id: impuesto.id,
            impuesto: impuesto.porcentaje ?? 0,
        });

        this.actualizarTotal();
    }
    onChangePreciosVenta = (id, value) => {
        this.setState(prevState => ({
            precios: prevState.precios.map(precio => {
                if (precio.id === id) {
                    precio.total = value;
                }
                return precio;
            }),
        }));
    };
    // Vaciar el precio paquete lo devuelve a calculado (precio unidad × cantidad).
    onChangePrecioPaquete = (id, value) => {
        this.setState(prevState => ({
            precios: prevState.precios.map(precio => {
                if (precio.id === id) {
                    precio.precio_paquete = value === "" ? null : value;
                }
                return precio;
            }),
        }));
    };
    actualizarTotal = async (ignoreTotals = false) => {
        switch (this.state.formaDeCalculo) {
            case 'total':
                await this.calcularPorTotal();
                break;
            case 'precio':
                await this.calcularPorPrecio();
                break;
            case 'costo':
            default:
                await this.calcularPorCosto();
                break;
        }

        const { item } = this.props;
        const { cantidad, costo, impuesto, descuento, impuesto_id, formaDeCalculo } = this.state;
        let imp = this.props.impuestos.filter(i => i.id === impuesto_id)[0];

        if (!imp && item.impuesto) {
            imp = item.impuesto;
        }

        const subTotal = costo * cantidad;
        const totalDesc = this.calcularDescuento(subTotal, descuento, cantidad);
        const totalImp = this.calcularImpuesto(subTotal - totalDesc, impuesto);
        const total = subTotal - totalDesc + totalImp;

        if (formaDeCalculo !== 'total') {
            this.setState({total});
        }

        this.props.updateTotal(
            item.id,
            total,
            {nombre: imp?.nombre ?? 'Ninguno', totalImpuesto: totalImp},
            totalDesc,
            ignoreTotals
        );

        this.updatePrecios();
    }

    updatePrecios = () => {
        this.setState(prevState => ({
            precios: prevState.precios.map(precio => {
                const imp = prevState.impuesto > 1 ? prevState.impuesto / 100 : prevState.impuesto;
                // Las listas de precio son de la unidad base: el costo de la línea
                // (por unidad de compra) se lleva a base antes del margen.
                const costoBase = parseFloat(prevState.costo) / (prevState.unidadSeleccionada?.factor || 1);
                const totalAnterior = parseFloat(precio.total);

                if (precio.porciento_beneficio && prevState.viejoCosto < costoBase) {
                    precio.total = redondear((costoBase + costoBase * parseFloat(precio.porciento_beneficio / 100)) * ((imp || 0) + 1));
                } else if (precio.precio_venta != null) {
                    precio.total = redondear(precio.precio_venta * ((imp || 0) + 1));
                }
                // Un precio agregado en esta compra (sin precio_venta) conserva
                // lo que se escribió.

                // Un precio de paquete propio se mueve en la misma proporción que
                // el precio unidad, para conservar el descuento por volumen.
                const totalNuevo = parseFloat(precio.total);
                if (tienePrecioPaquetePropio(precio) && totalAnterior > 0 && totalNuevo !== totalAnterior) {
                    precio.precio_paquete = redondear(precio.precio_paquete * (totalNuevo / totalAnterior));
                }

                return precio;
            })
        }));
    }

    onChangeItem = async (itemCompra = {}) => {
        await this.setState({
            id: itemCompra.id ? itemCompra.id : null,
            formaDeCalculo: 'total',
            nombre: itemCompra.nombre ? itemCompra.nombre : null,
            costo: itemCompra.costo || 0,
            precio: itemCompra.precio || 0,
            descuento: itemCompra.descuento || 0,
            viejoCosto: itemCompra.costo ? itemCompra.costo : null,
            cantidad: 1,
            referencia: itemCompra.referencia || '',
            impuesto: itemCompra.impuesto?.porcentaje || 0,
            impuesto_id: itemCompra.impuesto ? itemCompra.impuesto.id : 0,
            costoMin: itemCompra.min ? itemCompra.min : itemCompra.costo,
            disponible: itemCompra.cantidad ? itemCompra.cantidad : 0,
            precios: itemCompra.precios || [],
            grupo: itemCompra.grupo?.nombre,
            ...this.unidadesDeLinea(itemCompra),
            valid: itemCompra.cantidad > 0,
            total: itemCompra.total,
        });

        this.actualizarTotal();
    };
    getProductos = async (buscar = '') => {
        const table = await apiRequest({}, `productos?tipo=producto&limit=50&buscar=${buscar}`, 'get');
        if (table?.data) {
            return table.data.map((item) => ({
                value: item.id,
                label: item.referencia ? `${item.referencia.padEnd(8, ' ')} - ${item.nombre}` : item.nombre,
                producto: item,
            }));
        }
        return [];
    };
    // Opciones de unidad del producto y la que corresponde a la línea: la del
    // snapshot al editar una compra, la base al elegir un producto.
    unidadesDeLinea = (producto, linea = null) => {
        const opciones = opcionesUnidades(producto);
        const fila = linea ? filaUnidadDeLinea(normalizarUnidades(producto), linea) : null;
        const unidadSeleccionada =
            (fila && opciones.find((o) => o.value === fila.unidad)) ||
            opciones.find((o) => o.es_base) ||
            opciones[0];

        return { unidades: opciones, unidadSeleccionada };
    };
    // El costo se captura por unidad de compra: al cambiar de unidad se
    // re-escala (una CAJA de 24 cuesta 24 veces la UND) y se recalcula desde él.
    onChangeUnidad = async (opcion) => {
        const factorAnterior = this.state.unidadSeleccionada?.factor || 1;
        const factorNuevo = opcion?.factor || 1;

        await this.setState((prevState) => ({
            unidadSeleccionada: opcion,
            formaDeCalculo: 'costo',
            costo: (parseFloat(prevState.costo) || 0) / factorAnterior * factorNuevo,
        }));

        this.actualizarTotal();
    };
    addPrecio = () => {
        this.setState((prevState)=>({
            precios: [...prevState.precios, {id: Date.now()}]
        }));
    }
    removePrecio = (id) => {
        this.setState((prevState)=>({
            precios: prevState.precios.filter(p => p.id !== id)
        }));
    }
    toggleItem = () => {
        this.setState({
            isItem: !this.state.isItem,
        });
    };
    calcularTotal = (costo, cantidad, impuesto, descuento) => {
        const subTotal = costo * cantidad;
        const totalDescuento = this.calcularDescuento(subTotal, descuento, cantidad);
        const totalImpuesto = this.calcularImpuesto(subTotal, impuesto);

        return subTotal - totalDescuento + totalImpuesto;
    }
    calcularDescuento = (total, descuento, cantidad = 1) => {
        if (this.props.tipoDescuento === 'Porcentaje') {
            const porcentajeDesc = descuento ? descuento / 100 : 0;
            return total * porcentajeDesc;
        } else {
            return descuento * cantidad;
        }
    }
    calcularImpuesto = (total, impuesto) => {
        const p = impuesto < 1 ? impuesto : impuesto / 100;
        return total * p;
    }
    setFormaDeCalculo = (forma = 'costo') => {
        this.setState({
            formaDeCalculo: forma
        });
    }
    calcularPorCosto = async () => {
        const { costo, cantidad, impuesto, descuento } = this.state;

        const porcentajeImpuesto = parseFloat(impuesto > 1 ? (impuesto || 0) / 100 : impuesto);
        const desc = this.calcularDescuento(costo, descuento);
        const precio = (parseFloat(costo) - parseFloat(desc || 0)) * (porcentajeImpuesto + 1);
        const total = precio * cantidad;

        await this.setState({
            precio: precio,
            total: total
        });
    }
    calcularPorPrecio = async () => {
        const { precio, cantidad, impuesto, descuento } = this.state;

        const porcentajeImpuesto = parseFloat(impuesto > 1 ? (impuesto || 0) / 100 : impuesto);
        const total = precio * cantidad;
        const subcosto = precio / (porcentajeImpuesto + 1);
        const desc = this.calcularDescuento(subcosto, descuento);
        const costo = subcosto + parseFloat(desc || 0);

        await this.setState({
            costo: costo,
            total: total
        });
    }
    calcularPorTotal = async () => {
        const { total, cantidad, impuesto, descuento } = this.state;

        const porcentajeImpuesto = parseFloat(impuesto > 1 ? (impuesto || 0) / 100 : (impuesto || 0));
        const costo = (((total / cantidad)) / (porcentajeImpuesto + 1)) + parseFloat(descuento || 0);

        await this.setState({
            costo: costo,
            precio: total/cantidad
        });
    }
    render() {
        const { index, item, impuestos, removeItem, almacen, tipoDescuento, display } = this.props;
        const {
            cantidad,
            costo,
            impuesto_id,
            precios,
            precio,
            descuento,
            total,
            formaDeCalculo,
            selected,
            viejoCosto,
            disponible,
            unidades,
            unidadSeleccionada
        } = this.state;

        const factorLinea = unidadSeleccionada?.factor || 1;
        // El empaque del producto, si tiene: sus listas llevan precio unidad y precio paquete.
        const empaque = unidades.find((u) => !u.es_base && u.factor > 1);
        const unidadBase = unidades.find((u) => u.es_base) || unidades[0];
        // let total = this.calcularTotal(costo, cantidad, impuesto);
        let imp = impuesto_id ? impuestos.filter(i => i.id === impuesto_id)[0] : impuestos[0];

        if (!imp && item.impuesto) {
            imp = item.impuesto;
        }

        return (
            <tr>
                <td className='text-center'>
                    {index + 1}
                </td>
                <td>
                    <input type="hidden" name={`items_compra[p${index}][almacen_id]`} value={almacen || ''} />
                    <input type="hidden" name={`items_compra[p${index}][tipo]`} value={item.tipo ?? 'producto'} />
                    {item.item_id && (
                        <input type="hidden" name={`items_compra[p${index}][item_id]`} value={item.item_id} />
                        )}
                    {item.productoNuevo && (
                        <>
                            <input type="hidden" name={`items_compra[p${index}][referencia]`} value={item.productoNuevo?.referencia} />
                            <input type="hidden" name={`items_compra[p${index}][unidad]`} value={item.productoNuevo?.unidad?.value} />
                            <input type="hidden" name={`items_compra[p${index}][grupo_id]`} value={item.productoNuevo?.categoria?.value} />
                            {/* Presentaciones del producto nuevo; `unidad_id` es la unidad de la línea. */}
                            <input type="hidden" name={`items_compra[p${index}][nuevo_unidad_id]`} value={item.productoNuevo?.unidad?.id ?? ''} />
                            <input type="hidden" name={`items_compra[p${index}][nuevo_unidad_empaque_id]`} value={item.productoNuevo?.empaque?.id ?? ''} />
                            <input type="hidden" name={`items_compra[p${index}][nuevo_cantidad_empaque]`} value={item.productoNuevo?.empaque ? item.productoNuevo?.cantidadEmpaque ?? '' : ''} />
                        </>
                    )}
                    <div className="d-flex">
                        <div style={{flex: 1}}>
                            <SelectCreateAsync
                                cacheOptions
                                defaultOptions
                                loadOptions={this.getProductos}
                                loadingMessage={() => "Cargando productos..."}
                                value={selected}
                                onCreateOption={async label => {
                                    await this.props.editarProducto({nombre: label, impuesto: {}}, item.id);
                                    await this.setState({
                                        selected: {label, value: label},
                                        impuesto: { nombre: "Ninguno", porcentaje: 0, totalImpuesto: 0 }
                                    });
                                    this.onChangeItem();
                                }}
                                required
                                onChange={(e) => {
                                    if (!e) e = {};
                                    this.setState({selected: {label: e?.label, value: e?.value}})
                                    this.onChangeItem(e?.producto);
                                }}
                                name={`items_compra[p${index}][item_id]`}
                                isClearable={true}
                            />
                            {viejoCosto > 0 && (
                                <div style={{with: '100%'}}>
                                    <small>
                                        Últ. Precio{factorLinea > 1 ? ` (${unidadSeleccionada.value})` : ""}: {formatCurrency((parseFloat(viejoCosto) + this.calcularImpuesto(viejoCosto, imp.porcentaje)) * factorLinea)} | Displonible: {disponible}
                                    </small>
                                </div>
                            )}
                        </div>
                        {item?.productoNuevo?.nombre && (
                            <button type="button" className="btn" onClick={() => this.props.editarProducto(item.productoNuevo, item.id, true)}>
                                <i className="fa fa-pen"></i>
                            </button>
                        )}
                    </div>
                </td>
                <td>
                    <input
                        type='number'
                        name={`items_compra[p${index}][cantidad]`}
                        value={cantidad}
                        min='0.01'
                        onChange={(e) =>
                            this.onChangeInput("cantidad", e.target.value)
                        }
                        className='form-control text-right'
                        step='any'
                    />
                    <input type="hidden" name={`items_compra[p${index}][unidad_id]`} value={unidadSeleccionada?.unidad_id ?? ''} />
                    {unidades.length > 1 && (
                        <div className="m-t-5">
                            <Select
                                options={unidades}
                                value={unidadSeleccionada}
                                onChange={this.onChangeUnidad}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                    )}
                </td>
                <td onDoubleClick={() => this.setFormaDeCalculo('costo')} title="Doble Click para editar">
                    {formaDeCalculo === 'costo' ? (
                        <div className="input-group">
                            <div className="input-group-prepend">
                                <span className="input-group-text" id="basic-addon1">RD$</span>
                            </div>
                            <input
                                type='number'
                                name={`items_compra[p${index}][costo]`}
                                value={costo || ''}
                                onChange={(e) =>
                                    this.onChangeInput("costo", e.target.value)
                                }
                                className='form-control'
                                step='any'
                            />
                        </div>
                    ) : (
                        <div className="m-t-5">
                            <input type="hidden" name={`items_compra[p${index}][costo]`} value={costo} />
                            {formatCurrency(costo)}<i className="fa fa-pen table-edit-field color-primary"></i>
                        </div>
                    )}
                </td>
                <td className='text-center'>
                    <Select
                        options={impuestos.map(i => ({
                            label: i.nombre,
                            value: i.id
                        }))}
                        value={imp && {
                            label: imp?.nombre,
                            value: imp?.id,
                        }}
                        onChange={(e) =>
                            this.onChangeImpuesto(e.value)
                        }
                    />
                    <input type="hidden" name={`items_compra[p${index}][impuesto_id]`} value={impuesto_id || ''} />
                </td>
                <td className='text-center'>
                    <div className="input-group">
                        <div className="input-group">
                            {tipoDescuento === 'Monto Fijo' && (
                                <div className="input-group-append">
                                    <span className="input-group-text" id="basic-addon1">RD$</span>
                                </div>
                            )}
                            <input
                                type='number'
                                name={`items_compra[p${index}][descuento]`}
                                value={descuento}
                                min="0"
                                onChange={(e) =>
                                    this.onChangeInput("descuento", e.target.value)
                                }
                                className='form-control'
                                step='any'
                            />
                            {tipoDescuento === 'Porcentaje' && (
                                <div className="input-group-append">
                                    <span className="input-group-text" id="basic-addon1">%</span>
                                </div>
                            )}
                        </div>
                    </div>
                </td>
                <td onDoubleClick={() => this.setFormaDeCalculo('precio')} title="Doble Click para editar">
                    {formaDeCalculo === 'precio' ? (
                        <div className="input-group">
                            <div className="input-group-prepend">
                                <span className="input-group-text" id="basic-addon1">RD$</span>
                            </div>
                            <input
                                type='number'
                                name={`items_compra[p${index}][precio]`}
                                value={precio || ''}
                                onChange={(e) =>
                                    this.onChangeInput("precio", e.target.value)
                                }
                                className='form-control'
                                step='any'
                            />
                        </div>
                    ) : (
                        <div className="m-t-5">
                            <input type="hidden" name={`items_compra[p${index}][precio]`} value={precio} />
                            {formatCurrency(precio)}<i className="fa fa-pen table-edit-field color-primary"></i>
                        </div>
                    )}
                </td>
                <td
					className="text-right"
					onDoubleClick={() => this.setFormaDeCalculo('total')}
                    title="Doble Click para editar"
				>
					{formaDeCalculo === 'total' ? (
						<div className="input-group">
							<div className="input-group-prepend">
								<span
									className="input-group-text"
									id="basic-addon1"
								>
									RD$
								</span>
							</div>
							<input
								type="number"
								name={`items_compra[p${index}][total]`}
								value={total || 0}
								onChange={(e) =>
									this.onChangeInput("total", e.target.value)
								}
								className="form-control text-right"
								step="any"
							/>
						</div>
					) : (
						<div className="m-t-5">
                            {formatCurrency(total)}<i className="fa fa-pen table-edit-field color-primary"></i>
                        </div>
					)}
					<input
						type="hidden"
						name={`items_compra[p${index}][total]`}
						value={total || 0}
					/>
				</td>
                <td className='text-right'>
                    <div className="dropdown m-t-5">
                        <button className="dropdown-toggle" type="button"><small>PRECIOS ({precios.length})</small></button>
                        <div className="dropdown-menu dropdown-menu-right">
                            {empaque && (
                                <li className="dropdown-item d-flex align-center">
                                    <div className="p-r-15" style={{visibility: 'hidden'}}>#0</div>
                                    <small className="text-center" style={{minWidth: 150}}>{unidadBase?.value}</small>
                                    <small className="text-center p-l-10" style={{minWidth: 150}}>{empaque.value} (x{empaque.factor})</small>
                                </li>
                            )}
                            {precios.map((precio, i) => {
                                const paqueteCalculado = empaque ? redondear((parseFloat(precio.total) || 0) * empaque.factor) : null;
                                const paqueteMostrado = tienePrecioPaquetePropio(precio) ? precio.precio_paquete : paqueteCalculado;
                                // Igual al calculado se manda vacío: null = calculado.
                                const paqueteAEnviar = tienePrecioPaquetePropio(precio) && redondear(precio.precio_paquete) !== paqueteCalculado
                                    ? precio.precio_paquete
                                    : '';

                                return (
                                <li key={precio.id || i} className="dropdown-item d-flex align-center">
                                    <div className="text-left p-r-15">#{i + 1}</div> <input type="number" name={`items_compra[p${index}][precios][${i}][total]`} value={precio.total} onChange={e => this.onChangePreciosVenta(precio.id, e.target.value)} className="form-control text-right" style={{minWidth: 150}} title="Precio unidad" />
                                    {empaque && (
                                        <>
                                            <input type="number" value={paqueteMostrado ?? ''} onChange={e => this.onChangePrecioPaquete(precio.id, e.target.value)} className="form-control text-right m-l-10" style={{minWidth: 150}} title="Precio paquete (vacío = calculado)" />
                                            <input type="hidden" name={`items_compra[p${index}][precios][${i}][precio_paquete]`} value={paqueteAEnviar} />
                                        </>
                                    )}
                                    <button type="button" className="p-l-10"><i className="fas fa-trash" onClick={() => this.removePrecio(precio.id)}></i></button>
                                    <input type="hidden" name={`items_compra[p${index}][precios][${i}][id]`} value={precio.id} />
                                    <input type="hidden" name={`items_compra[p${index}][precios][${i}][precio_venta]`} value={precio.precio_venta} />
                                    <input type="hidden" name={`items_compra[p${index}][precios][${i}][porciento_beneficio]`} value={precio.porciento_beneficio} />
                                </li>
                            )})}
                            <li className="dropdown-divider"></li>
                            <li className="dropdown-item text-right"><button type="button" onClick={this.addPrecio}>Agregar +</button></li>
                        </div>
                    </div>
                </td>
                <td>
                    <button
                        type='button'
                        className='btn'
                        onClick={() => removeItem(item.id)}>
                        <i className='fas fa-trash'></i>
                    </button>
                </td>
            </tr>
        );
    }
}
